<template>
  <div>
    <div class="pt-4 pb-4 px-6">
      <div v-show="!showingAdd">
        <SecondaryButton @click="showAdd" class="w-full" :size="'lg'">Add New Address</SecondaryButton>
      </div>
      <fieldset v-show="showingAdd">
        <div>
          <div class="flex justify-between">
            <legend class="block text-md font-semibold tracking-tighter leading-5 text-gray-brand-900">
              {{ title }}
            </legend>
            <button
              v-if="passedDeliveryAddress"
              class="ml-2 inline-flex items-center shadow-sm p-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full bg-white hover:border-blue-600 transition-all duration-150 group-hover:opacity-100 text-gray-500 hover:text-blue-600"
              @click="updateCloseClicked()"
            >
              <XIcon class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <legend class="block mt-1 mb-3 text-sm tracking-wide leading-5 text-gray-brand-800">
            Enter your address to see if we can deliver to you.
          </legend>
        </div>

        <form class="mt-2 bg-white rounded-md shadow-sm">
          <div class="-mt-px flex">
            <div class="w-3/5 flex-1 min-w-0">
              <input
                id="address-line1"
                ref="addr1"
                v-model="address1"
                type="text"
                name="address-line1"
                autocomplete="address-line1"
                autocapitalize="words"
                aria-label="Street address"
                class="form-input relative block w-full rounded-none rounded-tl-lg bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                placeholder="Street address"
              />
            </div>
            <div class="w-2/5 -ml-px min-w-0">
              <input
                id="address-line2"
                ref="addr2"
                v-model="address2"
                type="text"
                autocomplete="address-line2"
                name="address-line2"
                aria-label="Unit"
                class="form-input relative block w-full rounded-none rounded-tr-lg bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                placeholder="Unit"
              />
            </div>
          </div>
          <div class="-mt-px flex">
            <div class="w-3/10 sm:w-2/5 flex-1 min-w-0">
              <input
                id="address-level2"
                v-model="city"
                type="text"
                autocomplete="address-level2"
                autocapitalize="words"
                name="address-level2"
                aria-label="City"
                class="form-input relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                placeholder="City"
              />
            </div>
            <div class="w-3/10 sm:w-1/5 -ml-px min-w-0">
              <select
                id="state"
                v-model="state"
                :key="`state-${merchantCountry}-${state}`"
                autocomplete="address-level1"
                name="state"
                class="form-select relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                placeholder="State"
                @change="onStateChange"
              >
                <option value="" disabled selected>{{ statePlaceholder }}</option>
                <template v-if="merchantCountry === 'USA'">
                  <option value="AK">AK</option>
                  <option value="AZ">AZ</option>
                  <option value="AL">AL</option>
                  <option value="AR">AR</option>
                  <option value="CA">CA</option>
                  <option value="CO">CO</option>
                  <option value="CT">CT</option>
                  <option value="DC">DC</option>
                  <option value="DE">DE</option>
                  <option value="FL">FL</option>
                  <option value="GA">GA</option>
                  <option value="HI">HI</option>
                  <option value="ID">ID</option>
                  <option value="IL">IL</option>
                  <option value="IN">IN</option>
                  <option value="IA">IA</option>
                  <option value="KS">KS</option>
                  <option value="KY">KY</option>
                  <option value="LA">LA</option>
                  <option value="ME">ME</option>
                  <option value="MD">MD</option>
                  <option value="MA">MA</option>
                  <option value="MI">MI</option>
                  <option value="MN">MN</option>
                  <option value="MS">MS</option>
                  <option value="MO">MO</option>
                  <option value="MT">MT</option>
                  <option value="NE">NE</option>
                  <option value="NV">NV</option>
                  <option value="NH">NH</option>
                  <option value="NJ">NJ</option>
                  <option value="NM">NM</option>
                  <option value="NY">NY</option>
                  <option value="NC">NC</option>
                  <option value="ND">ND</option>
                  <option value="OH">OH</option>
                  <option value="OK">OK</option>
                  <option value="OR">OR</option>
                  <option value="PA">PA</option>
                  <option value="RI">RI</option>
                  <option value="SC">SC</option>
                  <option value="SD">SD</option>
                  <option value="TN">TN</option>
                  <option value="TX">TX</option>
                  <option value="UT">UT</option>
                  <option value="VT">VT</option>
                  <option value="VA">VA</option>
                  <option value="WA">WA</option>
                  <option value="WV">WV</option>
                  <option value="WI">WI</option>
                  <option value="WY">WY</option>
                </template>
                <template v-if="merchantCountry === 'AUS'">
                  <option value="ACT">ACT</option>
                  <option value="NSW">NSW</option>
                  <option value="NT">NT</option>
                  <option value="QLD">QLD</option>
                  <option value="SA">SA</option>
                  <option value="TAS">TAS</option>
                  <option value="VIC">VIC</option>
                  <option value="WA">WA</option>
                </template>
                <template v-if="merchantCountry === 'CAN'">
                  <option value="AB">AB</option>
                  <option value="BC">BC</option>
                  <option value="MB">MB</option>
                  <option value="NB">NB</option>
                  <option value="NL">NL</option>
                  <option value="NS">NS</option>
                  <option value="NT">NT</option>
                  <option value="NU">NU</option>
                  <option value="ON">ON</option>
                  <option value="PE">PE</option>
                  <option value="QC">QC</option>
                  <option value="SK">SK</option>
                  <option value="YT">YT</option>
                </template>
                <template v-if="merchantCountry === 'GBR'">
                  <option value="BED">Bedfordshire</option>
                  <option value="BERK">Berkshire</option>
                  <option value="BUCK">Buckinghamshire</option>
                  <option value="CAM">Cambridgeshire</option>
                  <option value="CHES">Cheshire</option>
                  <option value="CORN">Cornwall</option>
                  <option value="CUMB">Cumbria</option>
                  <option value="DERB">Derbyshire</option>
                  <option value="DEV">Devon</option>
                  <option value="DOR">Dorset</option>
                  <option value="DUR">County Durham</option>
                  <option value="ERYORK">East Riding of Yorkshire</option>
                  <option value="ESX">East Sussex</option>
                  <option value="ESS">Essex</option>
                  <option value="GLS">Gloucestershire</option>
                  <option value="GMAN">Greater Manchester</option>
                  <option value="HANTS">Hampshire</option>
                  <option value="HERTS">Hertfordshire</option>
                  <option value="HANTS">Herefordshire</option>
                  <option value="IOW">Isle of Wight</option>
                  <option value="KENT">Kent</option>
                  <option value="LANCS">Lancashire</option>
                  <option value="LEICS">Leicestershire</option>
                  <option value="LINCS">Lincolnshire</option>
                  <option value="LOND">Greater London</option>
                  <option value="MER">Merseyside</option>
                  <option value="NORF">Norfolk</option>
                  <option value="NHANTS">Northamptonshire</option>
                  <option value="NTHUMB">Northumberland</option>
                  <option value="NOTTS">Nottinghamshire</option>
                  <option value="OXON">Oxfordshire</option>
                  <option value="RUT">Rutland</option>
                  <option value="SHROP">Shropshire</option>
                  <option value="SOM">Somerset</option>
                  <option value="STS">Staffordshire</option>
                  <option value="SUFF">Suffolk</option>
                  <option value="SURREY">Surrey</option>
                  <option value="TYNE">Tyne and Wear</option>
                  <option value="WARKS">Warwickshire</option>
                  <option value="W MIDS">West Midlands</option>
                  <option value="W SUSX">West Sussex</option>
                  <option value="WILTS">Wiltshire</option>
                  <option value="WORCS">Worcestershire</option>
                  <option value="YORK">North Yorkshire</option>
                  <option value="SYORK">South Yorkshire</option>
                  <option value="WYORK">West Yorkshire</option>
                  <option value="WALES">Wales</option>
                  <option value="SCOT">Scotland</option>
                  <option value="NIRE">Northern Ireland</option>
                </template>
              </select>
            </div>
            <div class="w-2/5 -ml-px min-w-0">
              <input
                id="postal-code"
                v-model="zip"
                autocomplete="postal-code"
                name="postal-code"
                aria-label="Postal code"
                class="form-input relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                placeholder="Postal code"
              />
            </div>
          </div>
          <div class="-mt-px flex">
            <div class="w-full flex-1 min-w-0">
              <input
                id="notes"
                v-model="notes"
                type="text"
                autocomplete="off"
                name="notes"
                aria-label="Notes"
                class="form-input relative block w-full rounded-none rounded-bl-md rounded-br-md bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                placeholder="Gate code or instructions (optional)"
              />
            </div>
          </div>
        </form>
        <div v-if="!isGiftOrder" class="mt-3 flex items-center justify-between">
          <div class="flex flex-col">
            <span class="text-sm font-medium text-gray-brand-900">Set as default delivery address</span>
          </div>
          <button
            type="button"
            role="switch"
            :aria-checked="isDefault"
            @click="isDefault = !isDefault"
            :class="[
              isDefault ? 'bg-blue-600' : 'bg-gray-200',
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
            ]"
          >
            <span
              aria-hidden="true"
              :class="[
                isDefault ? 'translate-x-5' : 'translate-x-0',
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
              ]"
            />
          </button>
        </div>
        <div
          v-show="hasErrorMessage"
          role="alert"
          class="mt-2 text-red-600 font-semibold text-sm rounded-md px-3 pt-1 pb-2 text-center"
        >
          {{ errorMessage }}
        </div>
        <div class="mt-2">
          <PrimaryButton
            @click="addAddress"
            class="w-full mt-2"
            :size="'xl'"
            :justify="'justify-center'"
            :active="validAddress == true"
            :streaking="validAddress == true"
            :is-loading="isAddingAddress"
          >
            {{ selectButtonTitle }}
          </PrimaryButton>
        </div>
      </fieldset>
    </div>
  </div>
</template>

<script>
import BasicButton from '@/components/Molecules/BasicButton'
import SecondaryButton from '@/components/Molecules/SecondaryButton'
import PrimaryButton from '../Molecules/PrimaryButton.vue'
import mapRelationship from '@/utils/mapRelationship'
import { methodHasFutureSlots } from '@/utils/fulfillmentSlots'
import { XIcon } from '@heroicons/vue/solid'
import { nextTick } from 'vue'
export default {
  name: 'AddDeliveryAddress',
  components: { BasicButton, XIcon, SecondaryButton, PrimaryButton },
  props: {
    passedBottle: {
      type: Object,
      required: true
    },
    passedDeliveryAddress: {
      type: Object,
      required: false
    },
    forceOpen: {
      type: Boolean,
      required: false,
      default: false
    },
    isGiftOrder: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    isDefault: false,
    isAddingAddress: false,
    errorMessage: '',
    replacingAddressId: undefined,
    showingAdd: false,
    latitude: null,
    longitude: null,
    geocodedAddress1: null
  }),
  computed: {
    statePlaceholder() {
      if (this.merchantCountry === 'USA') return 'State'
      if (this.merchantCountry === 'GBR') return 'County'
      return 'Province'
    },
    selectButtonTitle() {
      if (this.passedDeliveryAddress) {
        return 'Update & Select'
      } else {
        return 'Add & Select'
      }
    },
    title() {
      if (this.passedDeliveryAddress) {
        return 'Update an address'
      } else {
        return 'Add an address'
      }
    },
    fulfillmentSlot() {
      return this.$store.getters.fulfillmentSlot
    },
    // True iff any DELIVERY method in the bottle's possible_fulfillment_methods has a hasnt_started_yet slot.
    bottleHasAnyFutureDeliverySlots() {
      const stubs = this.passedBottle?.relationships?.possible_fulfillment_methods?.data || []
      const resolver = (stub) => mapRelationship(this.$store.state, stub)
      return stubs
        .map(resolver)
        .filter(Boolean)
        .filter((method) => method.attributes?.type !== 'PickupFulfillmentMethod')
        .some((method) => methodHasFutureSlots(method, resolver))
    },
    validAddress() {
      return (
        this.address1 &&
        this.city &&
        this.state &&
        this.zip.length >= { USA: 5, AUS: 4, CAN: 7, GBR: 7 }[this.merchantCountry]
      )
    },
    hasErrorMessage() {
      return this.errorMessage.length > 0
    },
    merchantCountry() {
      return mapRelationship(this.$store.state, this.passedBottle.relationships.merchant.data)?.attributes.country
    }
  },
  watch: {
    state(newValue, oldValue) {
      console.log('State changed from:', oldValue, 'to:', newValue)
    },
    passedDeliveryAddress(newValue) {
      if (newValue) {
        this.address1 = newValue.attributes.address1
        this.address2 = newValue.attributes.address2
        this.city = newValue.attributes.city
        this.state = newValue.attributes.state
        this.zip = newValue.attributes.zip
        this.notes = newValue.attributes.notes
        this.isDefault = !!newValue.attributes.is_default
        this.replacingAddressId = newValue.id
        this.showingAdd = true
        this.latitude = newValue.attributes.latitude ?? null
        this.longitude = newValue.attributes.longitude ?? null
        this.geocodedAddress1 = this.latitude != null ? newValue.attributes.address1 : null
      }
    }
  },
  mounted() {
    this.showingAdd = this.forceOpen
    google.maps.importLibrary('places').then(({ Autocomplete }) => {
      const autocomplete = new Autocomplete(this.$refs.addr1, {
        componentRestrictions: { country: { USA: 'us', AUS: 'au', CAN: 'ca', GBR: 'gb' }[this.merchantCountry] },
        fields: ['address_components', 'formatted_address', 'geometry', 'place_id']
      })
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        console.log('Place selected:', place)

        // Reset address fields
        this.address1 = ''
        this.address2 = ''
        this.city = ''
        this.state = ''
        this.zip = ''
        this.latitude = null
        this.longitude = null
        this.geocodedAddress1 = null

        for (const comp of place.address_components) {
          console.log('Processing component:', comp.long_name, 'types:', comp.types)
          // Check all types for each component
          for (const type of comp.types) {
            switch (type) {
              case 'street_number':
                this.address1 = comp.long_name
                break
              case 'route':
                if (this.address1) {
                  this.address1 += ' ' + comp.short_name
                } else {
                  this.address1 = comp.short_name
                }
                break
              case 'subpremise':
                this.address2 = comp.long_name
                break
              case 'locality':
                // This is usually the city (e.g., "London")
                if (!this.city) {
                  this.city = comp.long_name
                }
                break
              case 'sublocality_level_1':
                // For UK addresses, city might be in sublocality
                if (!this.city) {
                  this.city = comp.long_name
                }
                break
              case 'postal_town':
                // UK postal towns are often the city
                if (!this.city) {
                  this.city = comp.long_name
                }
                break
              case 'administrative_area_level_1':
                // For UK, this is usually the county/region (e.g., "Greater London")
                if (this.merchantCountry === 'GBR') {
                  // Only set state if we don't already have a valid county code
                  const mappedState = this.mapUKCountyToCode(comp.long_name)
                  console.log('Mapping UK county:', comp.long_name, 'to:', mappedState)
                  // Only update if we got a valid county code (not null)
                  if (mappedState !== null) {
                    this.state = mappedState
                    console.log('State set to:', this.state)
                  }
                } else {
                  this.state = comp.short_name
                }
                break
              case 'administrative_area_level_2':
                // For UK, this might contain additional county info
                if (this.merchantCountry === 'GBR' && !this.state) {
                  const mappedState = this.mapUKCountyToCode(comp.long_name)
                  if (mappedState !== null) {
                    this.state = mappedState
                  }
                }
                break
              case 'country':
                // Don't set state from country component
                break
              case 'postal_code':
                this.zip = comp.long_name
                break
            }
          }
        }

        // If we still don't have a city, try to extract from formatted address
        if (!this.city && place.formatted_address) {
          console.log('No city found in components, trying formatted address:', place.formatted_address)
          const parts = place.formatted_address.split(',')
          // For UK addresses, city is usually the second part after the street
          if (parts.length > 1) {
            this.city = parts[1].trim()
            console.log('Extracted city from formatted address:', this.city)
          }
        }

        const location = place.geometry && place.geometry.location
        if (location) {
          this.latitude = typeof location.lat === 'function' ? location.lat() : location.lat
          this.longitude = typeof location.lng === 'function' ? location.lng() : location.lng
          this.geocodedAddress1 = this.address1
        }

        nextTick(() => this.$refs.addr2.focus())
      })
    })
  },
  methods: {
    onStateChange() {
      console.log('State dropdown changed to:', this.state)
    },
    showAdd() {
      this.showingAdd = true
      nextTick(() => this.$refs.addr1.focus())
    },
    addAddress() {
      this.isAddingAddress = true
      const hasGeocode = this.latitude != null && this.longitude != null && this.geocodedAddress1 === this.address1
      this.$store
        .dispatch('setNewDeliveryAddress', {
          address1: this.address1,
          address2: this.address2,
          city: this.city,
          addressState: this.state,
          zip: this.zip,
          notes: this.notes,
          latitude: hasGeocode ? this.latitude : null,
          longitude: hasGeocode ? this.longitude : null,
          bottle: this.passedBottle,
          replacingAddressId: this.replacingAddressId,
          isDefault: this.isGiftOrder ? false : this.isDefault
        })
        .then(
          (response) => {
            // properly updated
            this.isAddingAddress = false
            this.alertUserIfNoFulfillmentSlot()
            this.clearFields()
            this.$emit('deliveryAddressEdited')
          },
          (error) => {
            // failed to update
            this.isAddingAddress = false
          }
        )
    },
    clearFields() {
      this.address1 = ''
      this.address2 = ''
      this.city = ''
      this.state = ''
      this.zip = ''
      this.notes = ''
      this.isDefault = false
      this.replacingAddressId = undefined
      this.latitude = null
      this.longitude = null
      this.geocodedAddress1 = null
    },
    updateCloseClicked() {
      this.clearFields()
      this.$emit('deliveryAddressEditCanceled')
    },
    alertUserIfNoFulfillmentSlot() {
      if (this.fulfillmentSlot) {
        this.errorMessage = ''
        return
      }
      if (this.bottleHasAnyFutureDeliverySlots) {
        // Address is in-zone; the delivery method is just closed. The inline "Opens [date]"
        // rows in DeliveryAddressChoice communicate this — don't contradict them with a
        // top-of-form "no delivery option" error.
        this.errorMessage = ''
        return
      }
      this.errorMessage = 'Sorry, no delivery option is currently available for that address.'
    },
    mapUKCountyToCode(countyName) {
      console.log('mapUKCountyToCode called with:', countyName)

      // Don't map country names
      const countryNames = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'United Kingdom', 'UK']
      if (countryNames.includes(countyName)) {
        console.log('Skipping country name:', countyName)
        return null
      }

      const countyMapping = {
        Bedfordshire: 'BED',
        Berkshire: 'BERK',
        Buckinghamshire: 'BUCK',
        Cambridgeshire: 'CAM',
        Cheshire: 'CHES',
        Cornwall: 'CORN',
        Cumbria: 'CUMB',
        Derbyshire: 'DERB',
        Devon: 'DEV',
        Dorset: 'DOR',
        'County Durham': 'DUR',
        'East Riding of Yorkshire': 'ERYORK',
        'East Sussex': 'ESX',
        Essex: 'ESS',
        Gloucestershire: 'GLS',
        'Greater Manchester': 'GMAN',
        Hampshire: 'HANTS',
        Hertfordshire: 'HERTS',
        Herefordshire: 'HANTS',
        'Isle of Wight': 'IOW',
        Kent: 'KENT',
        Lancashire: 'LANCS',
        Leicestershire: 'LEICS',
        Lincolnshire: 'LINCS',
        'Greater London': 'LOND',
        Merseyside: 'MER',
        Norfolk: 'NORF',
        Northamptonshire: 'NHANTS',
        Northumberland: 'NTHUMB',
        Nottinghamshire: 'NOTTS',
        Oxfordshire: 'OXON',
        Rutland: 'RUT',
        Shropshire: 'SHROP',
        Somerset: 'SOM',
        Staffordshire: 'STS',
        Suffolk: 'SUFF',
        Surrey: 'SURREY',
        'Tyne and Wear': 'TYNE',
        Warwickshire: 'WARKS',
        'West Midlands': 'W MIDS',
        'West Sussex': 'W SUSX',
        Wiltshire: 'WILTS',
        Worcestershire: 'WORCS',
        'North Yorkshire': 'YORK',
        'South Yorkshire': 'SYORK',
        'West Yorkshire': 'WYORK',
        Wales: 'WALES',
        Scotland: 'SCOT',
        'Northern Ireland': 'NIRE'
      }

      // Try exact match first
      if (countyMapping[countyName]) {
        console.log('Exact match found for:', countyName, '->', countyMapping[countyName])
        return countyMapping[countyName]
      }

      // Try partial matches for variations
      const lowerCountyName = countyName.toLowerCase()
      for (const [fullName, code] of Object.entries(countyMapping)) {
        if (lowerCountyName.includes(fullName.toLowerCase()) || fullName.toLowerCase().includes(lowerCountyName)) {
          console.log('Partial match found for:', countyName, '->', code, '(matched with:', fullName, ')')
          return code
        }
      }

      // If no match found, return null instead of the original name
      console.warn('No UK county mapping found for:', countyName)
      return null
    }
  }
}
</script>
<style>
.pac-container {
  @apply min-w-max;
  /* Must paint above the z-1000 QuestionModal when a delivery-slot question
     renders the address form inside it; without this it only wins by DOM order. */
  z-index: 1001;
}
</style>
